import { FormValues } from '../pages/MonetizationNew';

export type MonetizationOperationType = 'préstamo' | 'cheque' | 'pagaré' | 'commercial-endorsements';

const CHECK_LABELS = new Set(['cdp propios', 'cdp terceros', 'cheque']);
const PROMISSORY_NOTE_LABELS = new Set(['pagare dolar link', 'pagare en pesos', 'pagare']);
const LOAN_LABELS = new Set([
  'prestamo',
  'operaciones publicas',
  'obligacion negociable',
  'obligaciones negociables',
]);
const COMMERCIAL_ENDORSEMENT_LABELS = new Set(['aval comercial', 'commercial-endorsements', 'commercial endorsement']);

export const normalizeMonetizationLabel = (value?: string | null): string =>
  (value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

export const getMonetizationOperationType = (productLineLabel: string): MonetizationOperationType | string => {
  const label = normalizeMonetizationLabel(productLineLabel);

  if (CHECK_LABELS.has(label)) return 'cheque';
  if (PROMISSORY_NOTE_LABELS.has(label)) return 'pagaré';
  if (COMMERCIAL_ENDORSEMENT_LABELS.has(label)) return 'commercial-endorsements';
  if (LOAN_LABELS.has(label)) return 'préstamo';

  return label;
};

export const getEffectiveMonetizationOperationType = (
  operationType?: string,
  productLineLabel?: string
): MonetizationOperationType | string =>
  getMonetizationOperationType(operationType || productLineLabel || '');

const hasValue = (value: unknown) => value !== null && value !== undefined && value !== '';
const hasText = (value?: string | null) => !!value?.trim();

export const isMonetizationExecutionValid = (
  productLineLabel: string | undefined,
  values: Pick<
    FormValues,
    | 'monetization_date'
    | 'iva'
    | 'monetization_discount_rate'
    | 'monetization_interest'
    | 'clearing'
    | 'monetization_cvba_code'
    | 'buyer_alyc'
    | 'seller_alyc'
    | 'monetization_amount'
  >,
  mode: 'create' | 'edit'
): boolean => {
  if (mode !== 'edit') return false;

  const operationType = getMonetizationOperationType(productLineLabel ?? '');

  if (operationType === 'préstamo') {
    return hasText(values.monetization_date);
  }

  const commonExecutionFields =
    hasText(values.monetization_date) &&
    values.iva !== null &&
    values.iva !== undefined &&
    hasValue(values.monetization_discount_rate) &&
    hasValue(values.monetization_interest) &&
    values.clearing !== null &&
    values.clearing !== undefined &&
    hasText(values.buyer_alyc) &&
    hasText(values.seller_alyc);

  if (operationType === 'cheque' || operationType === 'pagaré') {
    return commonExecutionFields && hasText(values.monetization_cvba_code);
  }

  if (operationType === 'commercial-endorsements') {
    return commonExecutionFields && hasValue(values.monetization_amount);
  }

  return false;
};

export const didMutationPersist = (response: { success?: boolean; data?: { updated?: boolean } }): boolean =>
  Boolean(response.success && response.data?.updated !== false);

export const extractIdGlobal = (data: Record<string, unknown> | null | undefined): string | null => {
  if (!data) return null;
  const raw = data.idGlobal ?? data.id_global;
  if (raw == null) return null;
  const str = String(raw).trim();
  return str !== '' ? str : null;
};
